import React from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import logo from './img/logo.png';
import LandingPage from './pages/LandingPage';
import FaqPage from './pages/FaqPage';
import KoltoztetesPage from './pages/KoltoztetesPage';
import KoltoztetesQuotePage from './pages/KoltoztetesQuotePage';
import CsomagszallitasPage from './pages/CsomagszallitasPage';
import CsomagszallitasQuotePage from './pages/CsomagszallitasQuotePage';
import IkeaHazhozszallitasPage from './pages/IkeaHazhozszallitasPage';
import IkeaHazhozszallitasQuotePage from './pages/IkeaHazhozszallitasQuotePage';
import RaklaposSzallitasPage from './pages/RaklaposSzallitasPage';
import RaklaposSzallitasQuotePage from './pages/RaklaposSzallitasQuotePage';
import KerekparSzallitasPage from './pages/KerekparSzallitasPage';
import KerekparSzallitasQuotePage from './pages/KerekparSzallitasQuotePage';
import ButorSzallitasPage from './pages/ButorSzallitasPage';
import ButorSzallitasQuotePage from './pages/ButorSzallitasQuotePage';
import AjanlatPage from './pages/AjanlatPage';
import AdatkezelesiTajekoztatoPage from './pages/AdatkezelesiTajekoztatoPage';
import RolunkPage from './pages/RolunkPage';
import AdminQuoteEmailPanel from './pages/AdminQuoteEmailPanel';
import VeglegesMegrendelesPage from './pages/VeglegesMegrendelesPage';
import CampaignLayer from './components/CampaignLayer';
import CookieConsent, { COOKIE_SETTINGS_EVENT } from './components/CookieConsent';
import { CAMPAIGNS } from './pages/campaigns';
import ScrollToTop from './components/ScrollToTop';
import DocumentHead from './components/DocumentHead';
import ServiceFloatingQuoteCta from './components/ServiceFloatingQuoteCta';
import { I18nProvider, LANGS, useI18n } from './i18n';
import { AuthProvider } from './hooks/useAuth';
import { RequireAdmin, RequireAuth } from './components/AuthGuards';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import NotFoundPage from './pages/NotFoundPage';
import { SHOW_AKTUALIS_PROMOS } from './siteFlags';

function Nav() {
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang, t } = useI18n();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    // Close drawer on route change
    setMobileOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  React.useEffect(() => {
    document.body.classList.toggle('nav-mobile-open', mobileOpen);
    return () => document.body.classList.remove('nav-mobile-open');
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className={`nav${scrolled ? ' nav-scrolled' : ''}`}>
      <button
        className="logo"
        type="button"
        onClick={() => {
          navigate('/');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        <img src={logo} alt="Move and Parcel logó" />
      </button>

      <div className="nav-right">
        <div className="nav-item has-dropdown">
          <Link className="nav-pill" to="/" state={{ scrollTo: 'szolgaltatas' }}>
            {t('nav_services')}
          </Link>
          <div className="nav-dropdown">
            <Link to="/szolgaltatas/csomagszallitas">Csomagszállítás</Link>
            <Link to="/szolgaltatas/koltoztetes">Költöztetés</Link>
            <Link to="/szolgaltatas/raklapos-szallitas">Raklapos szállítás</Link>
            <Link to="/szolgaltatas/butor-szallitas">Bútorszállítás</Link>
            <Link to="/szolgaltatas/kerekpar-szallitas">Kerékpár–motorkerékpár szállítás</Link>
            <Link to="/szolgaltatas/ikea-hazhozszallitas">IKEA szállítás</Link>
            <Link to="/ajanlat/csomagszallitas">Különleges bánásmódot igénylő szállítás</Link>
          </div>
        </div>

        {SHOW_AKTUALIS_PROMOS ? (
          <Link className="nav-pill" to="/" state={{ scrollTo: 'akciok' }}>
            {t('nav_promos')}
          </Link>
        ) : null}
        <Link className="nav-pill" to="/" state={{ scrollTo: 'referenciak' }}>
          {t('nav_refs')}
        </Link>
        <Link className={`nav-pill${location.pathname === '/gyik' ? ' active' : ''}`} to="/gyik">
          {t('nav_faq')}
        </Link>
        <Link className="nav-pill" to="/" state={{ scrollTo: 'kapcsolat' }}>
          {t('nav_contact')}
        </Link>
        <Link className="nav-pill nav-pill-cta" to="/ajanlat">
          {t('nav_quote')}
        </Link>

        <select
          className="nav-lang"
          aria-label="Nyelv"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
        >
          {LANGS.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      <button
        type="button"
        className="nav-hamburger"
        aria-label={mobileOpen ? t('nav_close_menu') : t('nav_open_menu')}
        aria-expanded={mobileOpen}
        aria-controls="mobileNavDrawer"
        onClick={() => setMobileOpen((v) => !v)}
      >
        <span className="nav-hamburger-lines" aria-hidden="true" />
      </button>

      <div
        className={`nav-mobile-overlay${mobileOpen ? ' nav-mobile-overlay--open' : ''}`}
        onClick={closeMobile}
        aria-hidden={!mobileOpen}
      />

      <aside
        id="mobileNavDrawer"
        className={`nav-mobile-drawer${mobileOpen ? ' nav-mobile-drawer--open' : ''}`}
        aria-hidden={!mobileOpen}
      >
        <div className="nav-mobile-head">
          <div className="nav-mobile-title">{t('nav_menu')}</div>
          <button type="button" className="nav-mobile-close" onClick={closeMobile} aria-label={t('nav_close')}>
            ×
          </button>
        </div>

        <div className="nav-mobile-links">
          <Link className="nav-mobile-link" to="/ajanlat" onClick={closeMobile}>
            {t('nav_quote')}
          </Link>

          <div className="nav-mobile-section">
            <div className="nav-mobile-section-title">{t('nav_services')}</div>
            <Link className="nav-mobile-link" to="/szolgaltatas/csomagszallitas" onClick={closeMobile}>
              Csomagszállítás
            </Link>
            <Link className="nav-mobile-link" to="/szolgaltatas/koltoztetes" onClick={closeMobile}>
              Költöztetés
            </Link>
            <Link className="nav-mobile-link" to="/szolgaltatas/raklapos-szallitas" onClick={closeMobile}>
              Raklapos szállítás
            </Link>
            <Link className="nav-mobile-link" to="/szolgaltatas/butor-szallitas" onClick={closeMobile}>
              Bútorszállítás
            </Link>
            <Link className="nav-mobile-link" to="/szolgaltatas/kerekpar-szallitas" onClick={closeMobile}>
              Kerékpár–motorkerékpár szállítás
            </Link>
            <Link className="nav-mobile-link" to="/szolgaltatas/ikea-hazhozszallitas" onClick={closeMobile}>
              IKEA szállítás
            </Link>
            <Link className="nav-mobile-link" to="/ajanlat/csomagszallitas" onClick={closeMobile}>
              Különleges bánásmódot igénylő szállítás
            </Link>
          </div>

          {SHOW_AKTUALIS_PROMOS ? (
            <Link className="nav-mobile-link" to="/" state={{ scrollTo: 'akciok' }} onClick={closeMobile}>
              {t('nav_promos')}
            </Link>
          ) : null}
          <Link className="nav-mobile-link" to="/" state={{ scrollTo: 'referenciak' }} onClick={closeMobile}>
            {t('nav_refs')}
          </Link>
          <Link className="nav-mobile-link" to="/gyik" onClick={closeMobile}>
            {t('nav_faq')}
          </Link>
          <Link className="nav-mobile-link" to="/" state={{ scrollTo: 'kapcsolat' }} onClick={closeMobile}>
            {t('nav_contact')}
          </Link>

          <div className="nav-mobile-section">
            <div className="nav-mobile-section-title">Language</div>
            <select
              className="nav-lang nav-lang--mobile"
              aria-label="Language"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
            >
              {LANGS.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </aside>
    </nav>
  );
}

function AppFooter() {
  const { t } = useI18n();
  return (
    <footer className="footer">
      <div className="footer-inner">
        <p className="footer-copy">
          © {new Date().getFullYear()} Move and Parcel. {t('footer_rights')}
        </p>
        <nav className="footer-meta" aria-label={t('footer_legal_nav')}>
          <Link to="/adatkezelesi-tajekoztato">{t('footer_privacy')}</Link>
          <span className="footer-meta-sep" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            className="footer-cookie-btn"
            onClick={() => window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT))}
          >
            {t('footer_cookie_settings')}
          </button>
        </nav>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <div className="app">
          <DocumentHead />
          <ScrollToTop />
          <CampaignLayer campaigns={CAMPAIGNS} />
          <Nav />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/gyik" element={<FaqPage />} />
            <Route path="/rolunk" element={<RolunkPage />} />
            <Route path="/adatkezelesi-tajekoztato" element={<AdatkezelesiTajekoztatoPage />} />
            <Route path="/ajanlat" element={<AjanlatPage />} />
            <Route path="/szolgaltatas/koltoztetes" element={<KoltoztetesPage />} />
            <Route path="/ajanlat/koltoztetes" element={<KoltoztetesQuotePage />} />
            <Route path="/szolgaltatas/csomagszallitas" element={<CsomagszallitasPage />} />
            <Route path="/ajanlat/csomagszallitas" element={<CsomagszallitasQuotePage />} />
            <Route path="/vegleges-megrendeles" element={<VeglegesMegrendelesPage />} />
            <Route path="/szolgaltatas/ikea-hazhozszallitas" element={<IkeaHazhozszallitasPage />} />
            <Route path="/ajanlat/ikea-hazhozszallitas" element={<IkeaHazhozszallitasQuotePage />} />
            <Route path="/szolgaltatas/raklapos-szallitas" element={<RaklaposSzallitasPage />} />
            <Route path="/ajanlat/raklapos-szallitas" element={<RaklaposSzallitasQuotePage />} />
            <Route path="/szolgaltatas/kerekpar-szallitas" element={<KerekparSzallitasPage />} />
            <Route path="/ajanlat/kerekpar-szallitas" element={<KerekparSzallitasQuotePage />} />
            <Route path="/szolgaltatas/butor-szallitas" element={<ButorSzallitasPage />} />
            <Route path="/ajanlat/butor-szallitas" element={<ButorSzallitasQuotePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/blpkmap"
              element={
                <RequireAdmin>
                  <AdminQuoteEmailPanel />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <ServiceFloatingQuoteCta />
          <CookieConsent />
          <AppFooter />
        </div>
      </I18nProvider>
    </AuthProvider>
  );
}

